<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, nextTick, ref, watch } from 'vue';
import { OnClickOutside } from '@vueuse/components';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  options: { type: Array, required: true },
  modelValue: { type: [String, Number], default: '' },
  placeholder: { type: String, default: 'Выберите аккаунт' },
  searchPlaceholder: { type: String, default: 'Поиск по имени или @username' },
  ariaLabel: { type: String, default: 'Telegram-аккаунт' },
  disabled: { type: Boolean, default: false },
  compact: { type: Boolean, default: false },
  emptyState: { type: String, default: 'Аккаунты не найдены' },
  hasMore: { type: Boolean, default: false },
  loadingMore: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue', 'loadMore']);
const open = ref(false);
const search = ref('');
const activeIndex = ref(0);
const searchInput = ref(null);
const triggerButton = ref(null);

const selected = computed(() =>
  props.options.find(option => option.value === props.modelValue)
);
const normalizedSearch = computed(() => search.value.trim().toLowerCase());
const filteredOptions = computed(() => {
  const value = normalizedSearch.value.replace(/^@/, '');
  if (!value) return props.options;
  return props.options.filter(option => {
    const name = String(option.label || '').toLowerCase();
    const username = String(option.username || '').toLowerCase();
    const subtitle = String(option.subtitle || '').toLowerCase();
    return (
      name.includes(value) ||
      username.includes(value) ||
      subtitle.includes(value)
    );
  });
});

function usernameLabel(option) {
  if (option?.subtitle) return option.subtitle;
  return option?.username ? `@${option.username}` : 'Username не указан';
}

function openDropdown() {
  if (props.disabled) return;
  open.value = true;
  search.value = '';
  const selectedIndex = props.options.findIndex(
    option => option.value === props.modelValue
  );
  activeIndex.value = Math.max(0, selectedIndex);
  nextTick(() => searchInput.value?.focus());
}

function closeDropdown() {
  open.value = false;
  search.value = '';
}

function toggleDropdown() {
  if (open.value) closeDropdown();
  else openDropdown();
}

function selectOption(option) {
  emit('update:modelValue', option.value);
  closeDropdown();
  nextTick(() => triggerButton.value?.$el?.focus());
}

function moveActive(delta) {
  if (!filteredOptions.value.length) return;
  activeIndex.value =
    (activeIndex.value + delta + filteredOptions.value.length) %
    filteredOptions.value.length;
}

function selectActive() {
  const option = filteredOptions.value[activeIndex.value];
  if (option) selectOption(option);
}

watch(filteredOptions, () => {
  activeIndex.value = 0;
});
</script>

<template>
  <div
    class="relative w-full min-w-0"
    data-account-identity="tg_publication_account_identity"
  >
    <OnClickOutside @trigger="closeDropdown">
      <Button
        ref="triggerButton"
        type="button"
        variant="outline"
        :color="open ? 'blue' : 'slate'"
        size="lg"
        :disabled="disabled"
        no-animation
        class="!px-3 justify-between w-full text-n-slate-12 font-normal focus:outline-n-brand"
        :class="compact ? '!h-10' : '!h-14'"
        :aria-label="ariaLabel"
        aria-haspopup="listbox"
        :aria-expanded="open"
        @click="toggleDropdown"
        @keydown.down.prevent="open ? moveActive(1) : openDropdown()"
        @keydown.up.prevent="open ? moveActive(-1) : openDropdown()"
        @keydown.esc.prevent="closeDropdown"
      >
        <span v-if="selected" class="flex min-w-0 items-center gap-3 text-left">
          <slot name="leading" :option="selected">
            <span
              v-if="selected.icon"
              class="flex size-8 flex-shrink-0 items-center justify-center rounded-full bg-n-alpha-2 text-n-slate-11"
            >
              <span :class="selected.icon" class="size-4" />
            </span>
            <Avatar
              v-else
              :src="selected.avatarUrl || ''"
              :name="selected.label"
              :size="32"
              rounded-full
            />
          </slot>
          <span class="min-w-0 leading-tight">
            <span class="block truncate font-medium">{{ selected.label }}</span>
            <span
              v-if="!compact"
              class="block truncate text-xs text-n-slate-10"
            >
              {{ usernameLabel(selected) }}
            </span>
          </span>
        </span>
        <span v-else class="truncate text-n-slate-10">{{ placeholder }}</span>
        <span
          class="size-4 flex-shrink-0 text-n-slate-11"
          :class="open ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
        />
      </Button>

      <div
        v-if="open"
        class="absolute z-50 w-full mt-1 overflow-hidden border rounded-md shadow-lg bg-n-solid-1 border-n-strong"
      >
        <div class="relative border-b border-n-strong">
          <span class="absolute i-lucide-search top-2.5 size-4 left-3" />
          <input
            ref="searchInput"
            v-model="search"
            type="search"
            :placeholder="searchPlaceholder"
            :aria-label="searchPlaceholder"
            class="reset-base w-full py-2 pl-10 pr-2 text-sm focus:outline-none border-none rounded-t-md bg-n-solid-1 text-n-slate-12"
            @keydown.down.prevent="moveActive(1)"
            @keydown.up.prevent="moveActive(-1)"
            @keydown.enter.prevent="selectActive"
            @keydown.esc.prevent="closeDropdown"
          />
        </div>
        <ul class="py-1 mb-0 overflow-auto max-h-72" role="listbox">
          <li
            v-for="(option, index) in filteredOptions"
            :key="option.value"
            role="option"
            :aria-selected="option.value === modelValue"
          >
            <button
              type="button"
              class="flex w-full min-w-0 items-center gap-3 px-3 py-2 text-left transition-colors duration-150 hover:bg-n-alpha-2 focus-visible:bg-n-alpha-2 focus-visible:outline-none"
              :class="{
                'bg-n-alpha-2':
                  index === activeIndex || option.value === modelValue,
              }"
              @mouseenter="activeIndex = index"
              @click="selectOption(option)"
            >
              <slot name="leading" :option="option">
                <span
                  v-if="option.icon"
                  class="flex size-9 flex-shrink-0 items-center justify-center rounded-full bg-n-alpha-2 text-n-slate-11"
                >
                  <span :class="option.icon" class="size-4" />
                </span>
                <Avatar
                  v-else
                  :src="option.avatarUrl || ''"
                  :name="option.label"
                  :size="36"
                  rounded-full
                />
              </slot>
              <span class="min-w-0 flex-1 leading-tight">
                <span
                  class="block truncate text-sm text-n-slate-12"
                  :class="{ 'font-medium': option.value === modelValue }"
                >
                  {{ option.label }}
                </span>
                <span
                  v-if="!compact"
                  class="block truncate text-xs text-n-slate-10"
                >
                  {{ usernameLabel(option) }}
                </span>
              </span>
              <span
                v-if="option.value === modelValue"
                class="i-lucide-check size-4 flex-shrink-0 text-n-slate-11"
              />
            </button>
          </li>
          <li
            v-if="filteredOptions.length === 0"
            class="px-3 py-3 text-sm text-n-slate-11"
          >
            {{ emptyState }}
          </li>
        </ul>
        <Button
          v-if="hasMore"
          type="button"
          :label="loadingMore ? 'Загружаем…' : 'Ещё посты'"
          slate
          ghost
          class="w-full"
          :disabled="loadingMore"
          @click="emit('loadMore')"
        />
      </div>
    </OnClickOutside>
  </div>
</template>
